import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Fade,
  Switch,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Paper,
  styled,
  CircularProgress,
  Snackbar,
  Alert,
  TextField,
  IconButton,
  MenuItem,
  Select,
  FormControl
} from '@mui/material';
import {
  Group as TeamIcon,
  Person as SelfIcon,
  MonetizationOn as IncentiveIcon,
  CalendarMonth as MonthsIcon,
  Check as CheckIcon,
  Edit as EditIcon,
  Percent as PercentIcon,
  BusinessCenter as BasisIcon
} from '@mui/icons-material';

const SettingsSwitch = styled(Switch)(({ theme }) => ({
  width: 42,
  height: 24,
  padding: 0,
  '& .MuiSwitch-switchBase': {
    padding: 0,
    margin: 2,
    transitionDuration: '300ms',
    '&.Mui-checked': {
      transform: 'translateX(18px)',
      color: '#fff',
      '& + .MuiSwitch-track': {
        backgroundColor: '#10b981',
        opacity: 1,
        border: 0,
      },
    },
  },
  '& .MuiSwitch-thumb': {
    boxSizing: 'border-box',
    width: 20,
    height: 20,
  },
  '& .MuiSwitch-track': {
    borderRadius: 24 / 2,
    backgroundColor: '#E9E9EA',
    opacity: 1,
  },
}));

const IncentiveSetting = () => {
  const [settings, setSettings] = useState({ 
    team: true, 
    self: true, 
    payoutMonths: 3,
    minEligiblePercentage: 5,
    businessBasis: 'both'
  });
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [isEditingPayout, setIsEditingPayout] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get('/api/levels/settings');
      if (response.data.status) {
        const incentive = response.data.data.incentiveSettings;
        setSettings({
          team: incentive.enableTeamBusiness,
          self: incentive.enableSelfBusiness,
          payoutMonths: incentive.payoutMonths || 1,
          minEligiblePercentage: response.data.data.minEligiblePercentage || 0,
          businessBasis: incentive.businessBasis || 'both'
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      setNotification({ open: true, message: 'Failed to load settings', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates) => {
    const newSettings = { ...settings, ...updates };
    setUpdating(true);
    try {
      const current = await axios.get('/api/levels/settings');
      const payload = {
        ...current.data.data,
        minEligiblePercentage: Number(newSettings.minEligiblePercentage),
        incentiveSettings: {
          enableTeamBusiness: newSettings.team,
          enableSelfBusiness: newSettings.self,
          payoutMonths: Number(newSettings.payoutMonths),
          businessBasis: newSettings.businessBasis
        }
      };

      const response = await axios.put('/api/levels/settings', payload);
      if (response.data.status) {
        setSettings(newSettings);
        setIsEditingPayout(false);
        setNotification({ open: true, message: 'Settings updated successfully', severity: 'success' });
      }
    } catch (error) {
      console.error('Error updating settings:', error);
      setNotification({ open: true, message: 'Failed to update settings', severity: 'error' });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 3, md: 6 }, bgcolor: '#fff', minHeight: '100vh' }}>
      <Fade in timeout={500}>
        <Box sx={{ maxWidth: 800 }}>
          
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
            <IncentiveIcon sx={{ fontSize: 32, color: '#059669' }} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#1a1f2c' }}>
                Incentive Configuration
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage global distribution rules for cash-based performance incentives.
              </Typography>
            </Box>
          </Box>

          <Paper elevation={0} sx={{ border: '1px solid #f1f5f9', borderRadius: 4, overflow: 'hidden', position: 'relative' }}>
            {updating && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, bgcolor: 'rgba(255,255,255,0.5)', zIndex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}
            <List sx={{ p: 0 }}>
              <ListItem sx={{ py: 2.5, px: 3 }}>
                <TeamIcon sx={{ mr: 2, color: '#059669', fontSize: 20 }} />
                <ListItemText 
                  primary={<Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Team Wise Incentive</Typography>}
                  secondary="Calculate incentives based on group volume"
                />
                <ListItemSecondaryAction>
                  <SettingsSwitch checked={settings.team} onChange={() => updateSettings({ team: !settings.team })} disabled={updating} />
                </ListItemSecondaryAction>
              </ListItem>
              
              <Divider sx={{ mx: 3 }} />

              <ListItem sx={{ py: 2.5, px: 3 }}>
                <SelfIcon sx={{ mr: 2, color: '#0ea5e9', fontSize: 20 }} />
                <ListItemText 
                  primary={<Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Self Wise Incentive</Typography>}
                  secondary="Calculate incentives based on individual achievements"
                />
                <ListItemSecondaryAction>
                  <SettingsSwitch checked={settings.self} onChange={() => updateSettings({ self: !settings.self })} disabled={updating} />
                </ListItemSecondaryAction>
              </ListItem>

              <Divider sx={{ mx: 3 }} />

              <ListItem sx={{ py: 2.5, px: 3 }}>
                <MonthsIcon sx={{ mr: 2, color: '#6366f1', fontSize: 20 }} />
                <ListItemText 
                  primary={<Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Payout Months</Typography>}
                  secondary="Number of consecutive months for incentive payout"
                />
                <ListItemSecondaryAction>
                  <TextField
                    type="number"
                    size="small"
                    value={settings.payoutMonths}
                    onChange={(e) => setSettings({ ...settings, payoutMonths: e.target.value })}
                    sx={{ width: 100, '& input': { textAlign: 'center', fontWeight: 700 } }}
                    disabled={!isEditingPayout || updating}
                  />
                </ListItemSecondaryAction>
              </ListItem>

              <Divider sx={{ mx: 3 }} />

              <ListItem sx={{ py: 2.5, px: 3 }}>
                <BasisIcon sx={{ mr: 2, color: '#6366f1', fontSize: 20 }} />
                <ListItemText 
                  primary={<Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>Business Basis</Typography>}
                  secondary="Qualification criteria for incentives"
                />
                <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FormControl size="small" sx={{ minWidth: 100 }}>
                    <Select
                      value={settings.businessBasis}
                      onChange={(e) => setSettings({ ...settings, businessBasis: e.target.value })}
                      disabled={!isEditingPayout || updating}
                      sx={{ borderRadius: 2, fontWeight: 600 }}
                    >
                      <MenuItem value="both">Both</MenuItem>
                      <MenuItem value="team">Team Only</MenuItem>
                      <MenuItem value="self">Self Only</MenuItem>
                    </Select>
                  </FormControl>
                  {isEditingPayout ? (
                    <IconButton 
                      size="small" 
                      onClick={() => updateSettings({})}
                      disabled={updating}
                      sx={{ 
                        bgcolor: '#059669', 
                        color: '#fff', 
                        borderRadius: '8px',
                        '&:hover': { bgcolor: '#047857' },
                        '&.Mui-disabled': { bgcolor: '#e2e8f0' }
                      }}
                    >
                      <CheckIcon fontSize="small" />
                    </IconButton>
                  ) : (
                    <IconButton 
                      size="small" 
                      onClick={() => setIsEditingPayout(true)}
                      disabled={updating}
                      sx={{ 
                        bgcolor: '#f1f5f9', 
                        color: '#64748b', 
                        borderRadius: '8px',
                        '&:hover': { bgcolor: '#e2e8f0' }
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </ListItemSecondaryAction>
              </ListItem>
            </List>
          </Paper>

          <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #e2e8f0' }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              Operational Note: Incentive payouts are automatically adjusted based on these distribution rules.
            </Typography>
          </Box>

        </Box>
      </Fade>

      <Snackbar 
        open={notification.open} 
        autoHideDuration={4000} 
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={notification.severity} variant="filled" sx={{ width: '100%' }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default IncentiveSetting;
